type FastTrackerApi = {
  report: (event: string, payload: Record<string, unknown>, module: string, sendMode: number) => void;
  setEnable?: (enabled: 0 | 1) => void;
};

declare global {
  interface Window {
    __fastTrackerConfigName__?: string;
    __fastTracker__?: FastTrackerApi;
  }
}

type PendingEvent = {
  event: string;
  payload: Record<string, unknown>;
  module: string;
  urgent: boolean;
  expiresAt: number;
};

// Capture campaign dimensions when the event occurs, before an async SDK flush
// or a route change. Never copy arbitrary URL fields (which may contain emails).
export function keywordAttribution(search = "") {
  const params = new URLSearchParams(search);
  const fields: Record<string, string> = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
    const value = params.getAll(key).find(item => item.trim())?.trim();
    if (value && !/[{}]/.test(value)) fields[key] = value;
  }
  return fields;
}

// Reuse index.html's opt-in and SDK transport; never enable a disabled tracker.
export function createFastTrackerReporter(browser: Window) {
  let queue: PendingEvent[] = [];
  let retryTimer = 0;

  const flush = () => {
    browser.clearTimeout(retryTimer);
    retryTimer = 0;
    queue = queue.filter((item) => item.expiresAt > browser.performance.now());
    const tracker = browser.__fastTracker__;
    // The async loader installs a no-op report stub. Only the initialized
    // v1.0.1 API has setEnable; checking it does not change SDK configuration.
    if (typeof tracker?.report === "function" && typeof tracker.setEnable === "function") {
      const pending = queue;
      queue = [];
      for (const item of pending) {
        try {
          // SDK mode 2 flushes immediately using its page-exit transport.
          tracker.report(item.event, item.payload, item.module, item.urgent ? 2 : 0);
        } catch {
          // A telemetry failure must not interrupt the page. Do not retry a
          // possibly accepted report, which could duplicate its duration.
        }
      }
    }
    if (queue.length) retryTimer = browser.setTimeout(flush, 500);
  };

  return (event: string, payload: Record<string, unknown>, module: string, urgent = false) => {
    if (typeof browser.__fastTrackerConfigName__ !== "string") return;
    queue.push({ event, payload: { ...keywordAttribution(browser.location?.search), ...payload }, module, urgent, expiresAt: browser.performance.now() + 60_000 });
    // Keep only a bounded, short-lived buffer while the SDK loads/is blocked.
    if (queue.length > 100) queue.shift();
    flush();
  };
}

const reporters = new WeakMap<Window, ReturnType<typeof createFastTrackerReporter>>();

export function getFastTrackerReporter(browser: Window) {
  let reporter = reporters.get(browser);
  if (!reporter) {
    reporter = createFastTrackerReporter(browser);
    reporters.set(browser, reporter);
  }
  return reporter;
}
